import type { TreeItem } from '@unicorn/control';
import type { TicketType } from './types';

const parentIdPrefix = 'ticket-type-parent:';

export const getParentTreeItemId = (id: TicketType['id']) => `${parentIdPrefix}${id}`;

export const getValueId = (treeItemId: TreeItem['id']) => {
  const id = String(treeItemId);

  return id.startsWith(parentIdPrefix) ? id.slice(parentIdPrefix.length) : id;
};

export const getTicketTypesTree = (types: TicketType[]): TreeItem[] => {
  const typesByParentId = new Map<TicketType['parent_id'], TicketType[]>();

  types.forEach((type) => {
    const siblings = typesByParentId.get(type.parent_id);

    if (siblings) {
      siblings.push(type);
    } else {
      typesByParentId.set(type.parent_id, [type]);
    }
  });

  const getChildren = (parentId: TicketType['parent_id']): TreeItem[] =>
    (typesByParentId.get(parentId) ?? []).map(({ id, title }) => {
      const children = getChildren(id);

      return {
        id: children.length ? getParentTreeItemId(id) : String(id),
        label: title,
        children,
      };
    });

  return getChildren(null);
};

export const addFullySelectedParentIds = (
  items: TreeItem[],
  selectedIds: TreeItem['id'][]
) => {
  const selected = new Set(selectedIds.map(String));

  const visit = (item: TreeItem): { ids: string[]; fullySelected: boolean } => {
    const id = String(item.id);
    const children = item.children ?? [];

    if (!children.length) {
      return {
        ids: selected.has(id) ? [getValueId(id)] : [],
        fullySelected: selected.has(id),
      };
    }

    const childResults = children.map((child) => visit(child));
    const fullySelected = childResults.every((result) => result.fullySelected);
    const childIds = childResults.flatMap((result) => result.ids);

    return {
      ids: fullySelected ? [getValueId(id), ...childIds] : childIds,
      fullySelected,
    };
  };

  return items.flatMap((item) => visit(item).ids);
};
